/**
 * ESignPanel — Reusable embeddable "Send for Signature" widget.
 *
 * Drop-in usage:
 *   <ESignPanel parentType="work_order" parentId={job.id} defaultSigners={[...]} />
 *
 * Parent types supported by backend: work_order | tenant | sub_contractor
 *   | landlord | lease | estimate | inspection
 *
 * Shows:
 *   - Two-tab composer (Template ▸ pick from Dropbox Sign / One-off ▸ upload PDF)
 *   - Signer list with role/name/email fields (add/remove rows)
 *   - Sent-history list scoped to this parent record with per-row status,
 *     refresh, remind, cancel, download signed-PDF actions.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, Paperclip, PaperPlaneTilt, Plus, X, ArrowsClockwise,
  BellRinging, Prohibit, DownloadSimple, Warning, CheckCircle, Clock,
  UploadSimple, Sparkle,
} from "@phosphor-icons/react";

const STATUS_TONE = {
  sent:              { bg: "bg-cyan-100",   fg: "text-cyan-800",   icon: PaperPlaneTilt, label: "Sent" },
  viewed:            { bg: "bg-sky-100",    fg: "text-sky-800",    icon: Clock,          label: "Viewed" },
  partially_signed:  { bg: "bg-amber-100",  fg: "text-amber-800",  icon: Clock,          label: "Partial" },
  signed:            { bg: "bg-emerald-100",fg: "text-emerald-800",icon: CheckCircle,    label: "All signed" },
  downloadable:      { bg: "bg-emerald-100",fg: "text-emerald-800",icon: CheckCircle,    label: "Downloadable" },
  completed:         { bg: "bg-emerald-100",fg: "text-emerald-800",icon: CheckCircle,    label: "Completed" },
  declined:          { bg: "bg-rose-100",   fg: "text-rose-800",   icon: Prohibit,       label: "Declined" },
  canceled:          { bg: "bg-slate-200",  fg: "text-slate-700",  icon: Prohibit,       label: "Canceled" },
  bounce:            { bg: "bg-rose-100",   fg: "text-rose-800",   icon: Warning,        label: "Bounced" },
  error:             { bg: "bg-rose-100",   fg: "text-rose-800",   icon: Warning,        label: "Error" },
  unknown:           { bg: "bg-slate-100",  fg: "text-slate-700",  icon: Warning,        label: "Unknown" },
};

const emptySigner = () => ({ role: "", name: "", email_address: "" });

export default function ESignPanel({
  parentType,
  parentId,
  defaultSigners = [],
  defaultSubject = "Please sign",
  compact = false,
  templateKeyword = "",   // e.g. "lease" — auto-picks the first template whose title matches
}) {
  const [config, setConfig]   = useState(null);
  const [loadingCfg, setLC]   = useState(true);
  const [mode, setMode]       = useState("template"); // template | upload
  const [templateId, setTid]  = useState("");
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState("Please review and sign.");
  const [title, setTitle]     = useState("");
  const [pdf, setPdf]         = useState(null);
  const [useTags, setUseTags] = useState(false);
  const [signers, setSigners] = useState(defaultSigners.length ? defaultSigners : [emptySigner()]);
  const [sending, setSending] = useState(false);
  const [rows, setRows]       = useState([]);
  const [loadingRows, setLR]  = useState(true);
  const [expanded, setExp]    = useState(!compact);

  const currentTemplate = useMemo(
    () => (config?.templates || []).find(t => t.template_id === templateId),
    [config, templateId]
  );

  const loadConfig = useCallback(async () => {
    setLC(true);
    try {
      const { data } = await api.get("/esign/config");
      setConfig(data);
      if (data.templates?.length && !templateId) {
        // Prefer a template whose title contains the keyword (e.g. "lease")
        let picked = null;
        if (templateKeyword) {
          const kw = templateKeyword.toLowerCase();
          picked = data.templates.find(t => (t.title || "").toLowerCase().includes(kw));
        }
        setTid((picked || data.templates[0]).template_id);
      }
    } catch (e) {
      setConfig({ configured: false, templates: [] });
    } finally {
      setLC(false);
    }
  }, [templateId, templateKeyword]);

  const loadRows = useCallback(async () => {
    if (!parentType || !parentId) { setRows([]); setLR(false); return; }
    setLR(true);
    try {
      const { data } = await api.get("/esign/requests", {
        params: { parent_type: parentType, parent_id: parentId, page_size: 50 },
      });
      setRows(data.items || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLR(false);
    }
  }, [parentType, parentId]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadRows(); }, [loadRows]);

  // Sync template signer roles when template changes
  useEffect(() => {
    if (mode !== "template" || !currentTemplate) return;
    const roles = (currentTemplate.signer_roles || []).map(r => r.name);
    if (!roles.length) return;
    setSigners(prev => {
      const filled = [...prev];
      const out = roles.map((role, i) => {
        const cur = filled[i] || emptySigner();
        return { role, name: cur.name || "", email_address: cur.email_address || "" };
      });
      return out;
    });
  }, [mode, currentTemplate]);

  const updateSigner = (i, key, val) =>
    setSigners(s => s.map((x, j) => (j === i ? { ...x, [key]: val } : x)));
  const addSigner = () => setSigners(s => [...s, emptySigner()]);
  const removeSigner = (i) => setSigners(s => s.length > 1 ? s.filter((_, j) => j !== i) : s);

  const canSend = useMemo(() => {
    if (!config?.configured || sending) return false;
    if (!signers.length || signers.some(s => !s.name?.trim() || !s.email_address?.trim())) return false;
    if (mode === "template") return !!templateId;
    if (mode === "upload") return !!pdf;
    return false;
  }, [config, sending, signers, mode, templateId, pdf]);

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("parent_type", parentType);
      fd.append("parent_id",   parentId);
      fd.append("subject",     subject);
      fd.append("message",     message);
      fd.append("title",       title || subject);
      fd.append("signers_json", JSON.stringify(signers));

      let url;
      if (mode === "template") {
        fd.append("template_id", templateId);
        url = "/esign/send-template";
      } else {
        fd.append("pdf", pdf);
        if (useTags) fd.append("use_text_tags", "true");
        url = "/esign/send-upload";
      }
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sent for signature");
      setPdf(null);
      loadRows();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Failed: ${detail}`);
    } finally {
      setSending(false);
    }
  };

  const refreshRow = async (srid) => {
    try { await api.post(`/esign/requests/${srid}/refresh`); toast.success("Refreshed"); loadRows(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Refresh failed")); }
  };
  const remindSigner = async (srid, email) => {
    if (!email) return;
    try {
      const fd = new FormData(); fd.append("email_address", email);
      await api.post(`/esign/requests/${srid}/remind`, fd);
      toast.success(`Reminded ${email}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Remind failed")); }
  };
  const cancelRow = async (srid) => {
    if (!window.confirm("Cancel this signature request? Signers will lose access.")) return;
    try { await api.post(`/esign/requests/${srid}/cancel`); toast.success("Canceled"); loadRows(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Cancel failed")); }
  };
  const downloadPdf = (srid) => {
    const token = localStorage.getItem("access_token");
    const u = `${API}/esign/requests/${srid}/pdf`;
    // Use fetch to include Authorization header, then trigger download
    fetch(u, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error("Not ready"); return r.blob(); })
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${srid}.pdf`;
        a.click();
      })
      .catch(() => toast.error("Signed PDF not available yet"));
  };

  if (loadingCfg) {
    return <div className="p-4 text-sm text-slate-500">Loading e-sign…</div>;
  }

  return (
    <div className="rounded-2xl border-2 border-slate-200 bg-white overflow-hidden" data-testid="esign-panel">
      <button
        type="button"
        onClick={() => setExp(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-violet-700 text-white"
        data-testid="esign-panel-toggle"
      >
        <div className="flex items-center gap-2">
          <FileText size={22} weight="fill"/>
          <span className="font-black tracking-wide uppercase text-sm">E-Sign · Dropbox Sign</span>
          {config?.test_mode && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-400 text-black text-[10px] font-bold uppercase">
              Test mode
            </span>
          )}
        </div>
        <span className="text-xs opacity-80">{expanded ? "Hide" : "Show"}</span>
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          {!config?.configured && (
            <div className="rounded-xl bg-amber-50 border-2 border-amber-300 p-3 flex gap-2">
              <Warning size={22} className="text-amber-700 shrink-0"/>
              <div className="text-sm">
                <div className="font-bold text-amber-900">Dropbox Sign not configured</div>
                <div className="text-amber-800">
                  Add <code className="bg-amber-100 px-1 rounded">DROPBOX_SIGN_API_KEY</code> to
                  {" "}<code className="bg-amber-100 px-1 rounded">backend/.env</code>. Get your key at{" "}
                  <a href="https://app.hellosign.com/home/myAccount#api"
                     target="_blank" rel="noreferrer"
                     className="underline font-bold">app.hellosign.com → Account → API</a>.
                </div>
              </div>
            </div>
          )}
          {config?.configured && config?.error && (
            <div className="rounded-xl bg-rose-50 border-2 border-rose-300 p-3 text-sm text-rose-800">
              {config.error}
            </div>
          )}

          {/* Composer */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="flex gap-1 bg-white rounded-lg p-1 border border-slate-200 w-fit">
              <button
                type="button"
                onClick={() => setMode("template")}
                className={`px-3 py-1.5 rounded-md text-xs font-bold ${mode === "template" ? "bg-indigo-600 text-white" : "text-slate-600"}`}
                data-testid="esign-mode-template"
              >
                <Sparkle size={14} className="inline -mt-0.5 mr-1"/> Template
              </button>
              <button
                type="button"
                onClick={() => setMode("upload")}
                className={`px-3 py-1.5 rounded-md text-xs font-bold ${mode === "upload" ? "bg-indigo-600 text-white" : "text-slate-600"}`}
                data-testid="esign-mode-upload"
              >
                <UploadSimple size={14} className="inline -mt-0.5 mr-1"/> Upload PDF
              </button>
            </div>

            {mode === "template" && (
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase">Template</label>
                {(config?.templates || []).length === 0 ? (
                  <div className="text-sm text-slate-500 mt-1 rounded-lg bg-white border border-slate-200 p-2">
                    No templates found in your Dropbox Sign account.{" "}
                    <a href="https://app.hellosign.com/home/manageTemplates" target="_blank" rel="noreferrer"
                       className="underline text-indigo-600 font-bold">Create one →</a>
                  </div>
                ) : (
                  <select
                    className="mt-1 w-full rounded-lg border-2 border-slate-200 p-2 text-sm bg-white"
                    value={templateId}
                    onChange={(e) => setTid(e.target.value)}
                    data-testid="esign-template-select"
                  >
                    {(config.templates || []).map(t => (
                      <option key={t.template_id} value={t.template_id}>
                        {t.title || t.template_id}
                      </option>
                    ))}
                  </select>
                )}
                {currentTemplate?.signer_roles?.length > 0 && (
                  <div className="mt-1 text-[11px] text-slate-500">
                    Roles: {currentTemplate.signer_roles.map(r => r.name).join(" · ")}
                  </div>
                )}
              </div>
            )}

            {mode === "upload" && (
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase">PDF File</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdf(e.target.files?.[0] || null)}
                  className="mt-1 w-full text-sm"
                  data-testid="esign-pdf-input"
                />
                <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={useTags} onChange={e => setUseTags(e.target.checked)}
                    data-testid="esign-text-tags"/>
                  Use text tags (e.g. <code>[sig|req|signer1]</code>) already in the PDF
                </label>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase">Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)}
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 p-2 text-sm bg-white"
                  data-testid="esign-subject"/>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 uppercase">Document Title</label>
                <input value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="(defaults to Subject)"
                  className="mt-1 w-full rounded-lg border-2 border-slate-200 p-2 text-sm bg-white"
                  data-testid="esign-title"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase">Message</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-lg border-2 border-slate-200 p-2 text-sm bg-white"
                data-testid="esign-message"/>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold text-slate-600 uppercase">Signers</label>
                <button type="button" onClick={addSigner}
                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                  data-testid="esign-add-signer">
                  <Plus size={14} weight="bold"/> Add signer
                </button>
              </div>
              <div className="space-y-1.5">
                {signers.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                    <input value={s.role || ""} onChange={e => updateSigner(i, "role", e.target.value)}
                      placeholder="Role (Owner)"
                      className="col-span-3 rounded-lg border border-slate-200 p-1.5 text-xs bg-white"
                      data-testid={`esign-signer-role-${i}`}/>
                    <input value={s.name} onChange={e => updateSigner(i, "name", e.target.value)}
                      placeholder="Name"
                      className="col-span-4 rounded-lg border border-slate-200 p-1.5 text-xs bg-white"
                      data-testid={`esign-signer-name-${i}`}/>
                    <input value={s.email_address} onChange={e => updateSigner(i, "email_address", e.target.value)}
                      placeholder="Email"
                      type="email"
                      className="col-span-4 rounded-lg border border-slate-200 p-1.5 text-xs bg-white"
                      data-testid={`esign-signer-email-${i}`}/>
                    <button type="button" onClick={() => removeSigner(i)}
                      className="col-span-1 rounded-lg text-rose-500 hover:bg-rose-50 p-1 flex justify-center"
                      data-testid={`esign-signer-remove-${i}`}>
                      <X size={14} weight="bold"/>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={!canSend}
              className="w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-700 text-white font-black py-3 text-sm disabled:opacity-50 shadow-lg flex items-center justify-center gap-2"
              data-testid="esign-send-btn"
            >
              <PaperPlaneTilt size={18} weight="fill"/>
              {sending ? "Sending…" : "Send for Signature"}
            </button>
          </div>

          {/* History */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-xs font-black text-slate-500 uppercase tracking-wider">
                Sent for this record ({rows.length})
              </div>
              <button type="button" onClick={loadRows}
                className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"
                data-testid="esign-refresh-list">
                <ArrowsClockwise size={12}/> Refresh
              </button>
            </div>
            {loadingRows ? (
              <div className="text-xs text-slate-400 py-2">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="text-xs text-slate-400 py-4 text-center rounded-lg bg-slate-50 border border-dashed border-slate-200">
                Nothing sent yet for this record.
              </div>
            ) : (
              <div className="space-y-1.5">
                {rows.map((r) => <ESignRow key={r.signature_request_id} row={r}
                  onRefresh={() => refreshRow(r.signature_request_id)}
                  onRemind={(email) => remindSigner(r.signature_request_id, email)}
                  onCancel={() => cancelRow(r.signature_request_id)}
                  onDownload={() => downloadPdf(r.signature_request_id)}/>)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ESignRow({ row, onRefresh, onRemind, onCancel, onDownload }) {
  const [open, setOpen] = useState(false);
  const tone = STATUS_TONE[row.status] || STATUS_TONE.unknown;
  const StatusIcon = tone.icon;
  const isTerminal = ["signed", "downloadable", "completed", "canceled", "declined"].includes(row.status);
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="flex items-center gap-2 p-2">
        <span className={`inline-flex items-center gap-1 rounded-full ${tone.bg} ${tone.fg} px-2 py-0.5 text-[10px] font-black uppercase`}>
          <StatusIcon size={11} weight="fill"/> {tone.label}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-slate-800 truncate">{row.title || row.subject || row.signature_request_id}</div>
          <div className="text-[10px] text-slate-500 truncate">
            {row.signers?.length || 0} signer{(row.signers?.length || 0) === 1 ? "" : "s"} · {row.source}
            {row.test_mode ? " · TEST" : ""}
          </div>
        </div>
        <button type="button" onClick={() => setOpen(v => !v)}
          className="text-[10px] font-bold text-slate-500 hover:text-slate-800 px-1"
          data-testid={`esign-row-toggle-${row.signature_request_id}`}>
          {open ? "▲" : "▼"}
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-100 p-2 space-y-2 bg-slate-50">
          {(row.signer_statuses || row.signers || []).map((s, i) => {
            const email = s.signer_email_address || s.email_address;
            const name = s.signer_name || s.name;
            const role = s.signer_role || s.role;
            const signed = !!s.signed_at;
            return (
              <div key={i} className="flex items-center justify-between gap-2 text-xs">
                <div className="min-w-0">
                  <div className="font-bold text-slate-800 truncate">
                    {name} {role ? <span className="text-[10px] text-slate-500">· {role}</span> : null}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">{email}</div>
                </div>
                {signed ? (
                  <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-black uppercase">Signed</span>
                ) : (
                  <button type="button" onClick={() => onRemind(email)}
                    className="rounded-md bg-amber-100 hover:bg-amber-200 text-amber-900 px-2 py-1 text-[10px] font-bold flex items-center gap-1"
                    data-testid={`esign-remind-${row.signature_request_id}-${i}`}>
                    <BellRinging size={10} weight="fill"/> Remind
                  </button>
                )}
              </div>
            );
          })}
          <div className="flex flex-wrap gap-1.5 pt-1 border-t border-slate-200">
            <button type="button" onClick={onRefresh}
              className="rounded-md bg-white hover:bg-slate-100 border border-slate-200 px-2 py-1 text-[10px] font-bold flex items-center gap-1"
              data-testid={`esign-refresh-${row.signature_request_id}`}>
              <ArrowsClockwise size={10}/> Refresh
            </button>
            {(row.status === "signed" || row.status === "downloadable" || row.status === "completed") && (
              <button type="button" onClick={onDownload}
                className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 text-[10px] font-bold flex items-center gap-1"
                data-testid={`esign-download-${row.signature_request_id}`}>
                <DownloadSimple size={10} weight="fill"/> Signed PDF
              </button>
            )}
            {!isTerminal && (
              <button type="button" onClick={onCancel}
                className="rounded-md bg-rose-100 hover:bg-rose-200 text-rose-800 px-2 py-1 text-[10px] font-bold flex items-center gap-1"
                data-testid={`esign-cancel-${row.signature_request_id}`}>
                <Prohibit size={10} weight="fill"/> Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
